<template>
    <div :id="idPrefix + '-Area'" class="tree-area d-flex flex-column flex-grow-1 overflow-auto">
        <div :id="idPrefix + '-Tree'" style="list-style-type: none; padding: 8px;">
            <DivisionTreeItem :divItem="divSettings"
                              :selectDivision="selectDivision"
                              :disableDivisionIds="disableDivisionIds"
                              :isShowCheckIconEnableDivision="isShowCheckIconEnableDivision"
                              :viewableDivisionIds="viewableDivisionIds"
                              :initialCollapsedDivisionIds="initialCollapsedDivisionIds"
                              :isInheritViewable="isInheritViewable"
                              :mapDivisionIcon="mapDivisionIcon"
                              :isInheritance="isInheritance"
                              :nHierarchy="0"
                              :selectMode="selectMode"
                              :disableSelection="disableSelection"
                              :treeSelectionLinkage="treeSelectionLinkage"
                              :hideUnviewableDivisionSelection="hideUnviewableDivisionSelection"
                              :expandOnUncheck="expandOnUncheck"
                              @selectItem="onSelectItem"
                              @expanded="onExpanded"
                              @checkItem="checkItem"
                              @invalidSelect ="onInvalidSelect"
                              >
                <template v-for="(slot, name) in $slots" #[name]="data">
                    <slot :name="name" v-bind="data"></slot>
                </template>
            </DivisionTreeItem>
        </div>
    </div>
</template>

<script lang="ts">
    import { Vue, Options } from "vue-class-component";
    import { DivisionData } from "@/models/DivisionData"
    import { DependingDivisionData } from "@/models/Setting/DependingDivisionData"
    import DivisionTreeItem from "@/components/DivisionTree/DivisionTreeItem.vue"
    import { debounce } from 'lodash';
    import { TreeSelectionLinkage } from "@/components/DivisionTree/enums";

    @Options({
        components: {
            DivisionTreeItem,
        },
        props: {
            divSettings: { type: DivisionData, required: true },            // [in]（必須）部署情報一覧
            selectDivision: { type: DivisionData, default: new DivisionData() },        // [in]（selecteModが0の時、必須）選択中の部署情報
            arDisableDivisionID: { type: Array, default: [] },              // [in]（オプション）無効にする部署の部署ID
            isShowCheckIconEnableDivision: { type: Boolean, default: false },// [in] (オプション) 有効な部署にチェックアイコンを表示する
            arViewableDivisionID: { type: Array, default: null },           // [in] (オプション)閲覧可能部署ID
            isInheritViewable: { type: Boolean, default: true },            // [in] (オプション)閲覧設定を子要素に引き継ぐか
            defaultDivisionID: { type: Number, default: -1 },               // [in] (オプション)閲覧不可の部署を指定された際に初期選択する部署ID
            dependingDivisionData: {                                        // [in]（オプション）部署の継承情報
                type: DependingDivisionData,
                default: new DependingDivisionData(),
            },
            isInheritance: { type: Boolean, default: false },               // [in]（オプション）継承ツリーかどうか
            idPrefix: { type: String, default: "" },                        // [in] 部署ツリーIDのプレフィックス

            /*
             * （オプション）選択方法
             * 0 : 行選択（単一）
             * 1 : チェックボックス（複数）
             * 
             * 0の場合はpropsで「selectDivision」の指定が必須です。
             * 1の場合は
             */
            selectMode: { type: Number, default: 0 },
            disableSelection: { type: Boolean, default: false },            // [in]（オプション）部署の選択を無効化するかどうか
            treeSelectionLinkage: { type: Number, default: TreeSelectionLinkage.Normal },
            // 閲覧不可部署のチェック状態を非表示にするかどうか
            hideUnviewableDivisionSelection: { type: Boolean, default: true },
            // 初期表示で開閉したい部署IDリスト
            expandDivisionIds: { type: Array, default: [] },
            // 折りたたんでおく部署のルール
            foldingRule: { type: Function, default: () => []},
            // チェックが外れたら配下を展開するかどうか
            expandOnUncheck: { type: Boolean, default: false },
        },
        emits: [
            // selectItem - 選択が押下されたときのコールバック(第一引数：クリックされた部署データ DivisionData(参照))
            // expanded   - 開閉されたときのコールバック
            'selectItem',
            'expanded',
            /**
             * チェック通知 ※selectModeが1のときのみ通知される
             * 引数無し
             */
            'checkItem'
        ],
        watch: {
            /**
             * 選択部署変更監視
             */
            selectDivision: {
                handler: function () {
                    // 選択失敗状態をクリア
                    this.isInvalidSelect = false;
                }
            },
            /**
             * 部署情報監視
             */
            divSettings: {
                handler: function () {
                    // 選択部署指定後に部署リストが登録された際のチェック
                    this.checkSelectDivision();
                }
            },
            /**
             * 部署情報の子要素が登録されたことを監視
             */
            'divSettings.arChildren.length' :{
                handler: function () {
                    // 部署が設定された初回のみ、すべての子要素が閲覧不可の部署を閉じた状態とする。
                    // divSettingsはあとからLoadされることもあるが、divSettingsをdeepで監視するとパフォーマンスに懸念があるため子要素の数で監視する
                    this.setInitialCollapsedDivisionIds();
                }
            },
            'arDisableDivisionID': {
                handler: function () {
                    // 無効部署が途中で変更された場合は初回閉じる部署を再設定する
                    this.initialCollapsedDivisionIds.clear();
                    this.setInitialCollapsedDivisionIds();
                }
            },
            expandDivisionIds: {
                handler: function () {
                    this.initialCollapsedDivisionIds.clear();
                    this.setSpecificCollapsedDivisionIds();
                },
                immediate: true
            }
        }
    })

    export default class DivisionTreeSelect extends Vue {
        private readonly divSettings!: Readonly<DivisionData>;
        private readonly selectDivision!: Readonly<DivisionData>;
        private readonly arDisableDivisionID!: ReadonlyArray<number>;    		    // 選択不可にする部署ID
        private readonly arViewableDivisionID!: ReadonlyArray<number>;    		    // 閲覧可能部署ID
        private readonly isInheritViewable!: Readonly<boolean>;                     // 閲覧設定を子要素に引き継ぐか
        private readonly defaultDivisionID!: Readonly<number>;                      // 閲覧不可の部署を指定された際に初期選択する部署ID
        private readonly dependingDivisionData!: Readonly<DependingDivisionData>;   // 部署の継承情報
        private readonly disableSelection!: boolean;                                // 部署の選択を無効化するかどうか
        private mapDivisionIcon: Map<number, string> = new Map<number, string>();   // 部署IDとそのアイコン情報
        private isInheritance!: boolean;                                              // 継承ツリーかどうか
        private readonly idPrefix!: string;                                         // idプリフィックス(この画面を複数使用する際は必ず指定すること)
        private funcResize: any = null;
        private isInvalidSelect = false;
        private initialCollapsedDivisionIds = new Set<number>();                    // 初期閉じた状態とする部署ID
        private treeSelectionLinkage!: TreeSelectionLinkage;
        private hideUnviewableDivisionSelection!: boolean;
        private expandDivisionIds!: ReadonlyArray<number>;
        private foldingRule!: (root: DivisionData) => number[];
        private expandOnUncheck!: boolean;

        /**
        * 閲覧可能部署ID
        */
        private get viewableDivisionIds(): Set<number> | null {
            if (!this.arViewableDivisionID) {
                return null;
            }
            return new Set(this.arViewableDivisionID);
        }

        /**
        * 無効にする部署の部署ID
        */
        private get disableDivisionIds(): Set<number> {
            if (!this.arDisableDivisionID) {
                return new Set();
            }
            return new Set(this.arDisableDivisionID);
        }

        /**
        * 初期閉じた状態とする部署ID
        */
        private getInitialCollapsedDivisionIds(): Set<number> {
            const collapsedDivisionIds = new Set<number>();
            const foldingDivisionIds = this.foldingRule(this.divSettings);
            if (0 < foldingDivisionIds.length) {
                foldingDivisionIds.forEach(id => collapsedDivisionIds.add(id));
            } else {
                if (!this.divSettings || (!this.viewableDivisionIds && this.disableDivisionIds.size === 0)) {
                    return collapsedDivisionIds;
                }

                // 閲覧可能部署が指定されているときは閲覧不可の部署が対象、指定されていないときは無効部署リストが対象
                const disableIds = this.viewableDivisionIds ? this.divSettings.GetDisableDivisionID(this.viewableDivisionIds)
                                                            : this.disableDivisionIds;

                this.setCollapsedDivisionIds(this.divSettings, disableIds, collapsedDivisionIds);
            }

            return collapsedDivisionIds;
        }

        /**
         * コンポーネント作成時
         */
        public created(): void {
            if (0 != this.dependingDivisionData.mapDependingDivision.size) {
                this.setDivisionIcon(this.dependingDivisionData);
            }
        }

        /**
        * mounted
        */
        public mounted(): void {
            // 初回表示時、すべての子要素が閲覧不可の部署を閉じた状態とする。
            this.setInitialCollapsedDivisionIds();
        }

        /**
         * DOM再レンダリング後
         */
        public updated(): void {
            this.adjustTreeWidth();
        }

        /**
         * 表示時の処理
         */
        public show(): void {
            if (null == this.funcResize) {
                this.funcResize = debounce(this.adjustTreeWidth, 100);      // イベント削除時はインスタンスを合わせておかないと削除できない
                window.addEventListener('resize', this.funcResize);
            }
            this.adjustTreeWidth();
        }

        /**
         * 非表示時の処理
         */
        public hide(): void {
            // リサイズイベントを削除
            if (null != this.funcResize) {
                window.removeEventListener('resize', this.funcResize);
                this.funcResize = null;
            }
        }

        /**
         * ツリー要素の画面幅を調整します。
         */
        private adjustTreeWidth(): void {

            const area = document.getElementById(this.idPrefix + "-Area") as HTMLSelectElement;
            const tree = document.getElementById(this.idPrefix + "-Tree") as HTMLSelectElement;
            if (null == area || null == tree) {
                return;
            }

            // スクロールバーの有無の判定
            // clientHeightはスクロールバー(約16px)を含めていない値、offsetHeightは含めている値
            // ただし、border（前後合わせて2px）も含めているので前後5pxは許容範囲とする
            const diff = area.clientHeight - area.offsetHeight;
            if (((5 >= diff) && (0 <= diff)) || (((-5 <= diff) && (diff <= 0)))) {
                // 幅のスクロールが出てなければ、表示されている枠線（100%）の最大幅になる
                tree.style.width = "100%";
            }
            else {
                // 幅のスクロールが出ていたら、隠れている要素（maxcontent）が最大幅になる
                tree.style.width = "max-content";
            }
        }

        /**
         * 開閉イベント
         */
        private onExpanded(): void{
            this.adjustTreeWidth();
        }

        /**
         * チェック通知
         */
        private checkItem(selectDivision: DivisionData): void {
            this.$emit('checkItem', selectDivision);
        }

        /**
         * 選択部署の変更通知
         * @param[in] selectDivision : 選択された部署
         */
        private onSelectItem(selectDivision: Readonly<DivisionData>) {
            if (this.selectDivision.divisionId != selectDivision.divisionId) {
                // 親に通知
                this.$emit('selectItem', selectDivision);
            }
        }

        /**
         * 選択失敗通知
         */
        private onInvalidSelect(): void {
            this.isInvalidSelect = true;
            this.checkSelectDivision();
        }

        /**
         * 選択状態チェック
         */
        private checkSelectDivision(): void {
            // 選択に失敗している場合は初期部署を選択するように通知
            if (this.isInvalidSelect && 0 < this.defaultDivisionID) {
                const defaultDivision = this.divSettings.GetDivisionData(this.defaultDivisionID);
                if (defaultDivision) {
                    this.$emit('selectItem', defaultDivision);
                }
            }
        }

        /**
         * 継承情報からアイコンを作成する
         * @param[in] dependingData : 継承情報
         */
        public setDivisionIcon(dependingData: Readonly<DependingDivisionData>): void {
            this.mapDivisionIcon = new Map<number, string>();

            var arDivisionIDs = new Array<number>();
            this.divSettings.GetAllDivisionID(arDivisionIDs);

            // アイコン 奇数：継承元部署 偶数：継承先部署
            const arIcons = new Array<string>(
                'icon-font-blue',       'icon-font-blue-inherit',
                'icon-font-orange',     'icon-font-orange-inherit',
                'icon-font-green',      'icon-font-green-inherit',
                'icon-font-red2',       'icon-font-red2-inherit',
                'icon-font-lightblue',  'icon-font-lightblue-inherit',
                'icon-font-yellow',     'icon-font-yellow-inherit',
                'icon-font-purple',     'icon-font-purple-inherit',
                'icon-font-pink',       'icon-font-pink-inherit'
            );
            var mapDivisionIconIndex = new Map<number, number>();
            var nNextIconIndex = 0;

            // 継承元部署
            var i = 0;
            var nDependingID = undefined
            for (i = 0; i < arDivisionIDs.length; i++) {

                // 継承先を取得
                nDependingID = dependingData.mapDependingDivision.get(arDivisionIDs[i]);
                if (undefined == nDependingID) {
                    continue;
                }

                if (nDependingID == arDivisionIDs[i]) {
                    // 継承元部署

                    this.mapDivisionIcon.set(arDivisionIDs[i], arIcons[nNextIconIndex]);
                    mapDivisionIconIndex.set(arDivisionIDs[i], nNextIconIndex);

                    // nNextIconIndexを次に進める
                    nNextIconIndex = nNextIconIndex + 2;    // 次のIndexは子部署なので、二つ進める必要がある
                    if (arIcons.length <= nNextIconIndex) {
                        // 超えたら最初に戻す
                        nNextIconIndex = 0;
                    }
                }
                else {
                    // 継承先部署は継承元部署の登録が完了してからでないと、継承元部署のアイコンがわからないことがあるため、後で。
                }
            }

            // 継承先部署
            for (i = 0; i < arDivisionIDs.length; i++) {

                // 継承先を取得
                 nDependingID = dependingData.mapDependingDivision.get(arDivisionIDs[i]);
                if (undefined == nDependingID) {
                    continue;
                }

                if (nDependingID != arDivisionIDs[i]) {
                    // 継承先部署
                    const nIndex = mapDivisionIconIndex.get(nDependingID);
                    if (undefined == nIndex) {
                        // 見つからないことはありえない
                        continue;
                    }

                    this.mapDivisionIcon.set(arDivisionIDs[i], arIcons[nIndex + 1]);
                }
            }
        }

        /**
         * 初回閉じる部署IDを登録する
        */
        private setInitialCollapsedDivisionIds(): void {
            if (0 < this.initialCollapsedDivisionIds.size) {
                // 既に登録されている場合は処理なし
                return;
            }

            this.initialCollapsedDivisionIds = this.getInitialCollapsedDivisionIds();
        }

        /**
         * 初回閉じる部署IDを格納する(再帰関数)
        * @param[in]  targetDivision : 対象部署情報
        * @param[in]  disableIds : 無効な部署IDリスト
        * @param[out] collapsedDivisionIds : 初回閉じる部署IDを格納する変数
        * @returns 自身と子要素の中に一つでも有効な部署があればtrue
        */
        private setCollapsedDivisionIds(targetDivision: DivisionData, disableIds: Set<number>, collapsedDivisionIds: Set<number>): boolean {
            // 子要素を持たないときは自身の有効状態を返す
            const isEnable = !disableIds.has(targetDivision.divisionId);
            if (targetDivision.arChildren.length === 0) {
                return isEnable;
            }

            let isAllDisable: boolean = true;
            targetDivision.arChildren.forEach(child => {
                if (this.setCollapsedDivisionIds(child, disableIds, collapsedDivisionIds)) {
                    isAllDisable = false;
                }
            });

            // 自身と子要素のうち、すべてが無効状態の場合に、初回閉じる部署として格納する
            if (isAllDisable && !isEnable) {
                collapsedDivisionIds.add(targetDivision.divisionId);
            }

            return isEnable || !isAllDisable;
        }

        /**
         * 指定した部署ID以外閉じる
        */
        private setSpecificCollapsedDivisionIds(): void {
            if (this.expandDivisionIds.length <= 0) {
                // 設定されていない場合は何もしない
                return;
            }

            const expandDivisionList = new Set<number>();
            if(this.expandDivisionIds.length === 1 && this.expandDivisionIds[0] === 1){
                // ネットワーク全体のみの場合
                expandDivisionList.add(1);
            } else {
                this.expandDivisionIds.forEach(row => {
                    // ネットワーク全体以外がある場合
                    let parentDivision = this.divSettings.GetParentDivisionData(row);
                    if (parentDivision) {
                        // 親部署が見つかった場合
                        expandDivisionList.add(parentDivision.divisionId);

                        while (parentDivision !== undefined) {
                            if (parentDivision.divisionId === 1) {
                                // ネットワーク全体なら終了
                                break;
                            }
                            parentDivision = this.divSettings.GetParentDivisionData(parentDivision.divisionId);
                            if (parentDivision) {
                                // 親部署が見つかった場合
                                expandDivisionList.add(parentDivision.divisionId);
                            }
                        }
                    }
                })
            }

            // 全部署IDから開きたい部署IDを除いて閉じる部署IDのリストを作成する
            const allDivisionIds: Array<number> = [];
            this.divSettings.GetAllDivisionID(allDivisionIds);
            const collapsedIds = new Set<number>();
            allDivisionIds.forEach(id => {
                if (!expandDivisionList.has(id)) {
                    collapsedIds.add(id);
                }
            })
            this.initialCollapsedDivisionIds = collapsedIds;
        }
    }
</script>
